import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, HttpStatusCode } from "axios";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import LoginWithEmailAPIRequest from "users/_api/commands/login-with-email-api-request";
import ResetPasswordConfirmationAPIRequest from "users/_api/commands/reset-password-confirmation-api-request";
import ResetPasswordRequestAPIRequest from "users/_api/commands/reset-password-request-api-request";
import UserVendorViewAPIRequest from "users/_api/commands/user-vendor-view-api-request";
import { AuthContext } from "users/auth/context/auth-context";
import Password from "users/auth/credentials/passwords/password";
import UserCredentials, { InvalidCredentialsError } from "users/auth/credentials/user-credentials";
import { AuthInfo } from "users/auth/info/auth-info";
import AuthInfoSchema from "users/auth/info/auth-info-schema";
import UserAuthToken from "users/auth/tokens/user-auth-token";
import { User } from "users/user";

export type AuthResponse = {
  user: User;
  context: AuthContext;
  authToken: UserAuthToken;
  openEntityAgreements: UserOpenEntityAgreement[];
};

export default class AuthEmailAPIService {
  private static jsonHeaders(): AxiosHeaders {
    return Object.assign(new AxiosHeaders(), {
      "Content-Type": "application/json-patch+json",
    });
  }

  static async login(credentials: UserCredentials, rememberMe: boolean = false): Promise<AuthResponse> {
    try {
      const url = new URL("/users/login", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(url.toString(), new LoginWithEmailAPIRequest(credentials, rememberMe), {
        headers: this.jsonHeaders(),
      });

      const authInfo = new AuthInfo(AuthInfoSchema.deserialize(response.data));
      const user = authInfo.user;
      const context = authInfo.context;
      const authToken = authInfo.authToken;
      const openEntityAgreements = authInfo.openEntityAgreements;

      return {
        user,
        context,
        authToken,
        openEntityAgreements,
      };
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw new UserAPIServiceError("login", error);
      }
      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          console.warn(error.message);
          throw new InvalidCredentialsError();
        case HttpStatusCode.Unauthorized:
          throw new InvalidCredentialsError();
        default:
          throw new UserAPIServiceError("login", error);
      }
    }
  }

  static async requestPasswordReset(email: EmailAddress): Promise<void> {
    try {
      const url = new URL("/users/request-password-reset", AttorneyHubAPIService.apiBaseUrl);
      await axios.post(url.toString(), new ResetPasswordRequestAPIRequest(email), {
        headers: this.jsonHeaders(),
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new InvalidPasswordResetEmailError(email.value);
      } else {
        throw new UserAPIServiceError("requestPasswordReset", error);
      }
    }
  }

  static async confirmPasswordReset(password: Password, userId: Guid, token: string): Promise<AuthResponse> {
    try {
      const url = new URL("/users/confirm-password-reset", AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.post(
        url.toString(),
        new ResetPasswordConfirmationAPIRequest(password, userId, token),
        {
          headers: this.jsonHeaders(),
        },
      );

      const authInfo = new AuthInfo(AuthInfoSchema.deserialize(response.data));
      const user = authInfo.user;
      const context = authInfo.context;
      const authToken = authInfo.authToken;
      const openEntityAgreements = authInfo.openEntityAgreements;

      return {
        user,
        context,
        authToken,
        openEntityAgreements,
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        console.warn(error.message);
      }
      throw new UserAPIServiceError("confirmPasswordReset", error);
    }
  }

  static async updateUserVendorView(viewingAsVendor: boolean, authToken?: Readonly<UserAuthToken>): Promise<void> {
    if (!authToken) throw new Error("No auth token provided for updateUserVendorView");
    try {
      const url = new URL("/users/user-vendor-view", AttorneyHubAPIService.apiBaseUrl);
      await axios.patch(url.toString(), new UserVendorViewAPIRequest(viewingAsVendor), {
        headers: Object.assign(new AxiosHeaders(), {
          Authorization: `Bearer ${authToken.value}`,
        }),
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Unauthorized) {
        throw new ExpiredTokenError();
      }
      throw new UserAPIServiceError("updateUserVendorView", error);
    }
  }
}

export class ExpiredTokenError extends Error {
  constructor() {
    super("Expired token");
  }
}
export class InvalidPasswordResetEmailError extends Error {
  constructor(email: string) {
    super("Invalid email address: " + email);
  }
}

export class UserAPIServiceError extends Error {
  method: string;
  error: unknown;
  constructor(method: string, error: unknown) {
    super((error as Error)?.message ?? "An error occurred");
    this.method = method;
    this.error = error;
  }
}
